import { useCallback, useMemo, useState } from 'react';
import { useGetWarrantyReportQuery } from '../../../../api';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { formatDateForApi } from '../../../../utils/dateUtils';

export const ReportType = {
    AllWarrantyClaims: 1,
    MostReplacedParts: 2,
    DealerChassisCosts: 3
};

export default function useWarrantyReports() {
    const { getCurrentLanguageId } = useLanguage();
    const [reportType, setReportTypeState] = useState(ReportType.AllWarrantyClaims);
    const [lazyParams, setLazyParams] = useState({ first: 0, rows: 10, page: 1 });
    const [hasApplied, setHasApplied] = useState(false);

    const [activeFilters, setActiveFilters] = useState({
        itemCode: undefined,
        chassisNumber: undefined,
        dealerId: undefined,
        startDate: undefined,
        endDate: undefined,
        isDomestic: undefined
    });

    const queryArgs = useMemo(
        () => ({
            pageNumber: lazyParams.page || 1,
            pageSize: lazyParams.rows || 25,
            reportType: reportType || ReportType.AllWarrantyClaims,
            itemCode: reportType === ReportType.MostReplacedParts ? (activeFilters.itemCode ? String(activeFilters.itemCode).trim() : undefined) : undefined,
            chassisNumber: reportType === ReportType.MostReplacedParts ? (activeFilters.chassisNumber ? String(activeFilters.chassisNumber).trim() : undefined) : undefined,
            languageId: getCurrentLanguageId() || 1,
            dealerId: activeFilters.dealerId ?? undefined,
            startDate: formatDateForApi(activeFilters.startDate) || undefined,
            endDate: formatDateForApi(activeFilters.endDate) || undefined,
            isDomestic: activeFilters.isDomestic ?? undefined
        }),
        [lazyParams.page, lazyParams.rows, reportType, activeFilters, getCurrentLanguageId]
    );
    const { data, isFetching, isLoading, isError, error, refetch } = useGetWarrantyReportQuery(queryArgs, {
        skip: !hasApplied
    });
    const payload = hasApplied ? data?.data : null;
    const items = hasApplied ? (Array.isArray(payload?.items) ? payload.items : []) : [];
    const totalCount = hasApplied ? payload?.totalCount ?? 0 : 0;

    const onPage = (e) => {
        setLazyParams({ first: e.first, rows: e.rows, page: e.page + 1 });
    };

    const setReportType = (rt) => {
        setReportTypeState(rt);
        setLazyParams((p) => ({ ...p, first: 0, page: 1 }));
        setActiveFilters((f) => ({ ...f, itemCode: undefined }));
        setHasApplied(false);
    };

    const applyFilters = useCallback((filters) => {
        setActiveFilters({
            itemCode: filters.itemCode ?? undefined,
            chassisNumber: filters.chassisNumber ?? undefined,
            dealerId: filters.dealerId ?? undefined,
            startDate: filters.startDate ?? undefined,
            endDate: filters.endDate ?? undefined,
            isDomestic: filters.isDomestic ?? undefined
        });
        setLazyParams((p) => ({ ...p, first: 0, page: 1 }));
        setHasApplied(true);
    }, []);

    const resetFilters = useCallback(() => {
        setActiveFilters({
            itemCode: undefined,
            chassisNumber: undefined,
            dealerId: undefined,
            startDate: undefined,
            endDate: undefined,
            isDomestic: undefined
        });
        setLazyParams((p) => ({ ...p, first: 0, page: 1 }));
        setHasApplied(false);
    }, []);

    return {
        reportType,
        setReportType,
        items,
        totalCount,
        isFetching,
        isLoading,
        isError,
        error,
        refetch,
        lazyParams,
        onPage,
        activeFilters,
        applyFilters,
        resetFilters,
        queryArgs
    };
}
